import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Menu,
  Typography,
} from '@mui/material';

/**
 * Public-side replica of the agent's `ColumnVisibilityMenu`
 * (see `super-picker/components/siteplan/TopViewingSitePlanDetail.tsx` ~line 327).
 *
 * Renders a "Columns" outlined Button which opens a Menu listing every
 * column with a Checkbox and a "Reset" button to restore defaults.
 */

export type ColumnVisibilityState = Record<string, boolean>;

interface ColumnVisibilityMenuProps {
  anchorEl: HTMLElement | null;
  columns: readonly string[];
  getLabel: (column: string) => string;
  onClose: () => void;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onReset: () => void;
  onToggle: (column: string) => void;
  visibility: ColumnVisibilityState;
}

export const ColumnVisibilityMenu: React.FC<ColumnVisibilityMenuProps> = ({
  anchorEl,
  columns,
  getLabel,
  onClose,
  onOpen,
  onReset,
  onToggle,
  visibility,
}) => (
  <>
    <Button size="small" variant="outlined" onClick={onOpen}>
      Columns
    </Button>
    <Menu
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ minWidth: 220, px: 1.5, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Show Columns
          </Typography>
          <Button size="small" onClick={onReset}>
            Reset
          </Button>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <FormGroup>
          {columns.map((column) => (
            <FormControlLabel
              key={column}
              control={
                <Checkbox
                  size="small"
                  checked={visibility[column] !== false}
                  onChange={() => onToggle(column)}
                />
              }
              label={<Typography variant="body2">{getLabel(column)}</Typography>}
            />
          ))}
        </FormGroup>
      </Box>
    </Menu>
  </>
);

export default ColumnVisibilityMenu;
